import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Download, LayoutGrid, Activity, Code2, MonitorSmartphone, Wrench, Upload, Radio, RefreshCw, Warehouse, MapPin, CopyX, BarChart3, ScanLine, Menu, X, Ticket, UserCog, Package, Sun, Moon, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { can } from "@/lib/perms";
import api from "@/lib/api";

const RED = "#c00000";

function NavMenu({ label, Icon, items, testid }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  const active = list.some((it) => it.path === loc.pathname);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button data-testid={testid}
        className={`flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${open || active ? "bg-[#c00000] text-white" : "text-gray-300 hover:text-white hover:bg-white/10"}`}>
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 w-56 bg-[#1B1E23] border border-white/10 rounded-xl shadow-2xl p-1.5">
          {list.map((it) => {
            const on = loc.pathname === it.path;
            return (
              <button key={it.path} data-testid={it.testid} onClick={() => { setOpen(false); navigate(it.path); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${on ? "bg-white/10 text-white" : "text-gray-200 hover:bg-white/10"}`}>
                <it.Icon className="w-4 h-4" style={{ color: RED }} /> {it.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const loc = useLocation();
  const [toolsOpen, setToolsOpen] = useState(false);
  const [remoteOpen, setRemoteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openTickets, setOpenTickets] = useState(0);
  const [dark, setDark] = useState(() => typeof document !== "undefined" && document.documentElement.classList.contains("dark"));
  const toggleTheme = () => {
    const next = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", next);
    try { localStorage.setItem("fleet_theme", next ? "dark" : "light"); } catch (e) {}
    setDark(next);
  };

  const refreshTickets = useCallback(async () => {
    try { const r = await api.get("/tickets", { params: { status: "open" } }); setOpenTickets((r.data || []).length); }
    catch { /* ignoriert */ }
  }, []);
  useEffect(() => {
    refreshTickets();
    const iv = setInterval(refreshTickets, 30000);
    const h = () => refreshTickets();
    window.addEventListener("tickets:changed", h);
    return () => { clearInterval(iv); window.removeEventListener("tickets:changed", h); };
  }, [refreshTickets]);
  const emitTool = (name) => { setToolsOpen(false); navigate("/dashboard"); setTimeout(() => window.dispatchEvent(new CustomEvent(name)), 350); };
  const emitRemote = (name) => { setRemoteOpen(false); navigate("/dashboard"); setTimeout(() => window.dispatchEvent(new CustomEvent(name)), 350); };

  const navBtn = (path, label, Icon, testid) => {
    const active = loc.pathname === path;
    return (
      <button data-testid={testid} onClick={() => navigate(path)}
        className={`flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${active ? "bg-[#c00000] text-white" : "text-gray-300 hover:text-white hover:bg-white/10"}`}>
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </button>
    );
  };

  return (
    <header className="no-print sticky top-0 z-40 bg-[#1B1E23] border-b border-white/10">
      <div className="w-full px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <button onClick={() => navigate("/dashboard")} data-testid="logo-home" className="flex items-center gap-3 shrink-0 hover:opacity-90 transition-opacity">
          <svg viewBox="0 0 32 32" className="w-10 h-10" aria-hidden>
            <path d="M3 28 L15 4 L23 4 L11 28 Z" fill={RED} />
            <path d="M13 28 L25 4 L32 4 L20 28 Z" fill={RED} opacity="0.7" />
          </svg>
          <div className="leading-tight text-left">
            <div className="font-display font-bold text-base text-white">Fleet<span style={{ color: RED }}>Area</span></div>
            <div className="text-[10px] text-gray-300 font-mono uppercase tracking-wider">{t("subtitle")}</div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {can(user, "uebersicht") && navBtn("/dashboard", t("dashboard"), LayoutGrid, "nav-dashboard")}
          {can(user, "standorte") && navBtn("/locations", lang === "de" ? "Standorte" : "Locations", MapPin, "nav-locations")}
          {can(user, "uebersicht") && navBtn("/activity", t("activity"), Activity, "nav-activity")}
          <NavMenu testid="nav-geraete" label={lang === "de" ? "Lager" : "Stock"} Icon={Warehouse} items={[
            ...(can(user, "lager") ? [{ path: "/inventory", label: lang === "de" ? "Inventar" : "Inventory", Icon: Warehouse, testid: "nav-inventory" }] : []),
            ...(can(user, "scanner") ? [{ path: "/scanners", label: lang === "de" ? "Scanner" : "Scanners", Icon: ScanLine, testid: "nav-scanners" }] : []),
          ]} />
          <NavMenu testid="nav-agent-menu" label="Agent" Icon={Code2} items={[
            ...(can(user, "agent") ? [{ path: "/agent", label: lang === "de" ? "Agent-Skript" : "Agent script", Icon: Code2, testid: "nav-agent" }] : []),
            { path: "/install", label: t("installer"), Icon: Download, testid: "nav-installer" },
          ]} />
          {can(user, "berichte") && navBtn("/reports", lang === "de" ? "Berichte" : "Reports", BarChart3, "nav-reports")}
          {can(user, "tickets") && (
          <button data-testid="nav-tickets" onClick={() => navigate("/tickets")}
            className={`relative flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${loc.pathname === "/tickets" ? "bg-[#c00000] text-white" : "text-gray-300 hover:text-white hover:bg-white/10"}`}>
            <Ticket className="w-4 h-4" />
            <span>Tickets</span>
            {openTickets > 0 && (<span data-testid="nav-tickets-badge" className="ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold bg-amber-500 text-white">{openTickets}</span>)}
          </button>
          )}
          {can(user, "benutzer") && navBtn("/users", lang === "de" ? "Benutzer" : "Users", UserCog, "nav-users")}
          {can(user, "remote") && (
          <div className="relative" onMouseEnter={() => setRemoteOpen(true)} onMouseLeave={() => setRemoteOpen(false)}>
            <button data-testid="nav-remote" onClick={() => navigate("/tvmatch")}
              className={`flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${remoteOpen || loc.pathname === "/tvmatch" ? "bg-[#c00000] text-white" : "text-gray-300 hover:text-white hover:bg-white/10"}`}>
              <Radio className="w-4 h-4" />
              <span>Remote</span>
            </button>
            {remoteOpen && (
              <div data-testid="remote-menu" className="absolute right-0 top-full z-50 w-56 bg-[#1B1E23] border border-white/10 rounded-xl shadow-2xl p-1.5">
                <button data-testid="remote-tvmatch" onClick={() => { setRemoteOpen(false); navigate("/tvmatch"); }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/10 transition-colors">
                  <MonitorSmartphone className="w-4 h-4" style={{ color: RED }} /> {lang === "de" ? "TV-Abgleich" : "TV match"}
                </button>
                <button data-testid="remote-tv-register" onClick={() => emitRemote("remote:tv-register")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/10 transition-colors">
                  <RefreshCw className="w-4 h-4" style={{ color: RED }} /> {lang === "de" ? "TV registrieren" : "Register TV"}
                </button>
              </div>
            )}
          </div>
          )}
          {loc.pathname === "/dashboard" && can(user, "uebersicht") && (
            <div className="relative" onMouseEnter={() => setToolsOpen(true)} onMouseLeave={() => setToolsOpen(false)}>
              <button data-testid="nav-tools"
                className={`flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${toolsOpen ? "bg-[#c00000] text-white" : "text-gray-300 hover:text-white hover:bg-white/10"}`}>
                <Wrench className="w-4 h-4" />
                <span>Tools</span>
              </button>
              {toolsOpen && (
                <div data-testid="tools-menu" className="absolute right-0 top-full z-50 w-52 bg-[#1B1E23] border border-white/10 rounded-xl shadow-2xl p-1.5">
                    <button data-testid="tools-export" onClick={() => emitTool("tools:export")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/10 transition-colors">
                      <Download className="w-4 h-4" style={{ color: RED }} /> Export
                    </button>
                    <button data-testid="tools-import" onClick={() => emitTool("tools:import")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/10 transition-colors">
                      <Upload className="w-4 h-4" style={{ color: RED }} /> {t("import_data")}
                    </button>
                  <button data-testid="tools-duplicates" onClick={() => { setToolsOpen(false); navigate("/duplicates"); }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/10 transition-colors">
                    <CopyX className="w-4 h-4 text-amber-500" /> {lang === "de" ? "Duplikate" : "Duplicates"}
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen((v) => !v)} className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg border border-white/20 text-gray-300 hover:text-white hover:border-white/40 transition-colors">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <button data-testid="theme-toggle" onClick={toggleTheme} title={dark ? (lang === "de" ? "Hell-Modus" : "Light mode") : (lang === "de" ? "Dunkel-Modus" : "Dark mode")} className="flex items-center justify-center w-9 h-9 rounded-lg border border-white/20 text-gray-300 hover:text-white hover:border-white/40 transition-colors">
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button data-testid="lang-toggle" onClick={() => setLang(lang === "de" ? "en" : "de")} className="text-xs font-mono px-2.5 py-1.5 rounded-md border border-white/20 text-gray-300 hover:text-white hover:border-white/40 transition-colors">
            {lang === "de" ? "EN" : "DE"}
          </button>
          <div className="hidden md:block text-right leading-tight mr-1">
            <div className="text-xs text-gray-200 font-medium">{user?.name}</div>
            <div className="text-[10px] text-gray-300 font-mono">{user?.email}</div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#c00000] px-3 py-1.5 rounded-lg hover:bg-[#c00000]/10 transition-colors">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">{t("logout")}</span>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div data-testid="mobile-nav-panel" className="lg:hidden fixed top-16 left-0 right-0 z-50 bg-[#1B1E23] border-b border-white/10 max-h-[calc(100vh-4rem)] overflow-y-auto p-3 space-y-1 shadow-2xl">
            {[
              ["/dashboard", t("dashboard"), LayoutGrid, "m-nav-dashboard", "uebersicht"],
              ["/locations", lang === "de" ? "Standorte" : "Locations", MapPin, "m-nav-locations", "standorte"],
              ["/activity", t("activity"), Activity, "m-nav-activity", "uebersicht"],
              ["/agent", lang === "de" ? "Agent-Skript" : "Agent script", Code2, "m-nav-agent", "agent"],
              ["/inventory", lang === "de" ? "Lager" : "Stock", Warehouse, "m-nav-inventory", "lager"],
              ["/scanners", lang === "de" ? "Scanner" : "Scanners", ScanLine, "m-nav-scanners", "scanner"],
              ["/reports", lang === "de" ? "Berichte" : "Reports", BarChart3, "m-nav-reports", "berichte"],
              ["/tickets", "Tickets", Ticket, "m-nav-tickets", "tickets"],
              ...(can(user, "benutzer") ? [["/users", lang === "de" ? "Benutzer" : "Users", UserCog, "m-nav-users", "benutzer"]] : []),
              ["/install", t("installer"), Download, "m-nav-installer", null],
              ...(can(user, "remote") ? [["/tvmatch", lang === "de" ? "TV-Abgleich" : "TV match", MonitorSmartphone, "m-nav-tvmatch", "remote"]] : []),
              ...(can(user, "uebersicht") ? [["/duplicates", lang === "de" ? "Duplikate" : "Duplicates", CopyX, "m-nav-duplicates", "uebersicht"]] : []),
            ].filter(([path, label, Icon, tid, area]) => !area || can(user, area)).map(([path, label, Icon, tid]) => {
              const active = loc.pathname === path;
              return (
                <button key={path} data-testid={tid} onClick={() => { setMobileOpen(false); navigate(path); }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${active ? "bg-[#c00000] text-white" : "text-gray-200 hover:bg-white/10"}`}>
                  <Icon className="w-4 h-4 shrink-0" /> {label}
                  {path === "/tickets" && openTickets > 0 && (<span className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-bold bg-amber-500 text-white">{openTickets}</span>)}
                </button>
              );
            })}
            {user?.email && (<div className="pt-2 mt-2 border-t border-white/10 text-[11px] text-gray-300 font-mono px-3 truncate">{user.email}</div>)}
          </div>
        </>
      )}
    </header>
  );
}
